// Market benchmarks for the reports, from TMX Money without a key. The S&P/TSX Composite comes back as the
// index itself (^TSX); the US indices are known to TMX by name (^DJI:US, ^SPX:US) but carry no prices, so the
// three US lines use the ETFs that track them. A closing report used to depend on a web search finding the
// closes, and on some evenings it found nothing verifiable; this block is in the context every time.
import { t } from '@/i18n';
import type { MessageKey } from '@/i18n';
import type { Quote } from '@/types';
import { formatNYClock, todayNY } from './marketHours';
import { tmxQuotes } from './tmx';

export interface BenchmarkSpec {
  key: string;
  symbol: string;
  labelKey: MessageKey;
  /** The ETF standing in for the index; absent when the quote is the index itself. */
  proxy?: string;
}

export const BENCHMARK_SPECS: BenchmarkSpec[] = [
  { key: 'bench:spx', symbol: 'SPY:US', labelKey: 'bench.spx', proxy: 'SPY' },
  { key: 'bench:dji', symbol: 'DIA:US', labelKey: 'bench.dji', proxy: 'DIA' },
  { key: 'bench:nasdaq', symbol: 'ONEQ:US', labelKey: 'bench.nasdaq', proxy: 'ONEQ' },
  { key: 'bench:tsx', symbol: '^TSX', labelKey: 'bench.tsx' },
];

export interface Benchmark {
  spec: BenchmarkSpec;
  quote: Quote;
}

/** Never throws: a report without benchmarks is still a report. The TSX line is only fetched for Canadian holdings. */
export async function fetchBenchmarks(opts: { canada: boolean }, signal?: AbortSignal): Promise<Benchmark[]> {
  const specs = BENCHMARK_SPECS.filter((s) => opts.canada || s.key !== 'bench:tsx');
  try {
    const { quotes } = await tmxQuotes(specs.map((s) => ({ key: s.key, symbol: s.symbol, kind: 'equity' as const })), signal);
    return specs.flatMap((spec) => {
      const quote = quotes.find((q) => q.key === spec.key);
      return quote ? [{ spec, quote }] : [];
    });
  } catch {
    return [];
  }
}

/** Pure; exported for tests. The block appended to the holdings context, in the UI language. */
export function formatBenchmarks(list: Benchmark[]): string {
  if (list.length === 0) return '';
  const signed = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
  const level = (v: number) => v.toFixed(2);
  const lines = [t('ctx.benchHead')];
  for (const { spec, quote } of list) {
    const name = spec.proxy ? t('ctx.benchProxy', { label: t(spec.labelKey), proxy: spec.proxy }) : t(spec.labelKey);
    const parts: string[] = [];
    if (quote.changePct != null) parts.push(t('ctx.today', { pct: signed(quote.changePct) }));
    // An index level is the number readers know; an ETF price is not, so proxies carry only the change.
    if (!spec.proxy) {
      parts.push(t('ctx.benchLevel', { level: level(quote.price) }));
      if (quote.open != null) parts.push(t('ctx.open', { price: level(quote.open) }));
      if (quote.low != null && quote.high != null) parts.push(t('ctx.range', { low: level(quote.low), high: level(quote.high) }));
      if (quote.previousClose != null) parts.push(t('ctx.prevClose', { price: level(quote.previousClose) }));
    }
    parts.push(t('ctx.benchAt', { at: `${todayNY(quote.asOf).slice(5)} ${formatNYClock(quote.asOf)}` }));
    lines.push(`- ${name}: ${parts.join(', ')}`);
  }
  return lines.join('\n');
}
